import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
    BookOpen, Search, Calendar, Eye, ChevronRight, 
    Loader2, Filter, Tag
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import SEOHead from '../components/SEOHead';

export default function BlogPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const selectedCategory = searchParams.get('category') || '';

    useEffect(() => {
        loadData();
    }, [selectedCategory]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [articlesData, categoriesData] = await Promise.all([
                apiRequest(`/blog/articles${selectedCategory ? `?category=${selectedCategory}` : ''}`),
                apiRequest('/blog/categories')
            ]);
            setArticles(articlesData);
            setCategories(categoriesData);
        } catch (error) {
            console.error('Failed to load blog data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category) => {
        if (category) {
            setSearchParams({ category });
        } else {
            setSearchParams({});
        }
    };

    const filteredArticles = articles.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="blog-page">
            {/* SEO */}
            <SEOHead 
                title="Conseils Piscine & Guides d'Entretien | O.C.A Piscine"
                description="Découvrez nos guides et conseils pour l'entretien de votre piscine : traitement de l'eau, filtration, hivernage, problèmes courants. Expertise professionnelle."
                keywords="conseils piscine, guide entretien piscine, traitement eau piscine, problème piscine, filtre à sable, hivernage piscine"
                canonicalUrl="https://www.ocapiscine.fr/blog"
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <BookOpen className="h-10 w-10 text-primary" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">
                            Conseils & Guides Piscine
                        </h1>
                    </div>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Découvrez nos articles et guides pour entretenir parfaitement votre piscine. 
                        Conseils d'experts, solutions aux problèmes courants et astuces pratiques.
                    </p>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher un article..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-12 rounded-full"
                            data-testid="blog-search"
                        />
                    </div>

                    {/* Categories */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        <Button
                            variant={selectedCategory === '' ? 'default' : 'outline'}
                            onClick={() => handleCategoryChange('')}
                            className="rounded-full whitespace-nowrap"
                            size="sm"
                        >
                            <Filter className="h-4 w-4 mr-1" />
                            Tous
                        </Button>
                        {categories.map(cat => (
                            <Button
                                key={cat.name}
                                variant={selectedCategory === cat.name ? 'default' : 'outline'}
                                onClick={() => handleCategoryChange(cat.name)}
                                className="rounded-full whitespace-nowrap"
                                size="sm"
                            >
                                {cat.name} ({cat.count})
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Articles Grid */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : filteredArticles.length === 0 ? (
                    <Card className="glass rounded-2xl">
                        <CardContent className="py-12 text-center">
                            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                            <p className="text-muted-foreground">Aucun article trouvé</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredArticles.map(article => (
                            <Link 
                                key={article.id} 
                                to={`/blog/${article.slug}`}
                                data-testid={`article-${article.slug}`}
                            >
                                <Card className="glass rounded-2xl h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                                    {/* Image */}
                                    {article.image_url && (
                                        <div className="aspect-video overflow-hidden">
                                            <img 
                                                src={article.image_url} 
                                                alt={`${article.title} - Blog O.C.A Piscine`}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                        </div>
                                    )}
                                    
                                    <CardContent className="p-6">
                                        {/* Category Badge */}
                                        <Badge variant="secondary" className="mb-3">
                                            {article.category}
                                        </Badge>
                                        
                                        {/* Title */}
                                        <h2 className="font-heading font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                                            {article.title}
                                        </h2>
                                        
                                        {/* Excerpt */}
                                        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                                            {article.excerpt}
                                        </p>
                                        
                                        {/* Meta */}
                                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="h-3 w-3" />
                                                {formatDate(article.created_at)}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Eye className="h-3 w-3" />
                                                {article.views || 0} vues
                                            </div>
                                        </div>
                                        
                                        {/* Tags */}
                                        {article.tags && article.tags.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-3">
                                                {article.tags.slice(0, 3).map(tag => (
                                                    <span 
                                                        key={tag} 
                                                        className="inline-flex items-center text-xs bg-muted px-2 py-0.5 rounded-full"
                                                    >
                                                        <Tag className="h-2.5 w-2.5 mr-1" />
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Read More */}
                                        <div className="flex items-center gap-1 text-primary text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                                            Lire l'article
                                            <ChevronRight className="h-4 w-4" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}

                {/* CTA Section */}
                <Card className="glass rounded-3xl mt-12 overflow-hidden">
                    <CardContent className="p-8 text-center">
                        <h2 className="font-heading text-2xl font-bold mb-4">
                            Besoin de conseils personnalisés ?
                        </h2>
                        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                            Notre assistant IA est disponible 24/7 pour répondre à toutes vos questions 
                            sur l'entretien de votre piscine.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/chat">
                                <Button className="rounded-full">
                                    Poser une question à l'IA
                                    <ChevronRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <Link to="/analysis">
                                <Button variant="outline" className="rounded-full">
                                    Analyser mon eau
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
